import type { Address, Chain } from 'viem'

import { addressesRegistry, ChainId } from '@morpho-org/blue-sdk'
import { ChainUtils, isDefined } from '@morpho-org/morpho-ts'
import {
  type ChainConfig,
  chains,
  MERKL_DISTRIBUTOR_ADDRESS,
  type MorphoContractsByVersion,
  ChainId as Web3ChainId
} from '@repo/web3/configs'

import { COMMON_PROTOCOLS, type Protocol, type ProtocolName } from './protocols'

export type VaultVersion = 'v1.0' | 'v1.1'

const ethAddresses = addressesRegistry[ChainId.EthMainnet]
const baseAddresses = addressesRegistry[ChainId.BaseMainnet]
const polygonAddresses = addressesRegistry[ChainId.PolygonMainnet]
const unichainAddresses = addressesRegistry[ChainId.Unichain]
const katanaAddresses = addressesRegistry[ChainId.KatanaMainnet]
const arbitrumAddresses = addressesRegistry[ChainId.ArbitrumMainnet]
const hyperliquidAddresses = addressesRegistry[ChainId.HyperliquidMainnet]
const optimismAddresses = addressesRegistry[ChainId.OptimismMainnet]
const monadAddresses = addressesRegistry[ChainId.MonadMainnet]
const worldchainAddresses = addressesRegistry[ChainId.WorldChainMainnet]
const plumeAddresses = addressesRegistry[ChainId.PlumeMainnet]

function getMetaMorphoFactories(chainId: Web3ChainId): Record<VaultVersion, Address | null> {
  const { MetaMorphoV10Factory, MetaMorphoV11Factory }: MorphoContractsByVersion['v1'] =
    chains[chainId].custom.contracts.v1
  return {
    'v1.0': MetaMorphoV10Factory?.address ?? null,
    'v1.1': MetaMorphoV11Factory?.address ?? null
  }
}

interface ChainColorByTheme {
  light: string
  dark: string
}

export interface InterfaceChain {
  chain: Chain
  chainIdentifier: string
  isPreLiquidationSupported: boolean
  protocols: Record<ProtocolName, Protocol>
  withSimplePermit: Set<Address>
  /** ERC-20 the chain charges gas in (e.g. USDC on Arc). Flows spending it keep a gas reserve back. */
  gasToken?: { address: Address; decimals: number }
  safeIdentifier: string | undefined
  vaultVersion: {
    [key in VaultVersion]: Address | null
  }
  merklAddress: Address
  color: ChainColorByTheme
  wrappedTokensToPeripheralSymbols: Record<Address, string[]>
  /** Recently added chain, highlighted with a "New" badge in network selectors. */
  isNew?: boolean
}

/** Vaults app-only chain fields; everything else comes from the shared `@repo/web3` chain. */
interface ChainOverlay {
  /** Display name, when the Vaults app shows a different one than the shared chain. */
  name?: string
  color: ChainColorByTheme
  /** Pre-liquidation rollout flag, not derived from deployments. */
  isPreLiquidationSupported?: boolean
  protocols?: Partial<Record<ProtocolName, Protocol>>
  wrappedTokensToPeripheralSymbols?: Record<Address, string[]>
  isNew?: boolean
}

const DEFAULT_PROTOCOLS: Record<ProtocolName, Protocol> = {
  aaveV3Optimizer: COMMON_PROTOCOLS.aaveV3Optimizer,
  aaveV2: COMMON_PROTOCOLS.aaveV2,
  aaveV3: COMMON_PROTOCOLS.aaveV3,
  compoundV2: COMMON_PROTOCOLS.compoundV2,
  compoundV3: COMMON_PROTOCOLS.compoundV3
}

const vaultsChains = {
  [Web3ChainId.Mainnet]: {
    isPreLiquidationSupported: true,
    color: { light: '#555E64', dark: '#d6d6d6' },
    wrappedTokensToPeripheralSymbols: {
      [ethAddresses.wNative]: ['ETH'],
      [ethAddresses.wstEth]: ['stETH', 'WETH', 'ETH'],
      [ethAddresses.bIB01]: ['bib01'],
      [ethAddresses.wbC3M]: ['bC3M']
    }
  },
  [Web3ChainId.Base]: {
    isPreLiquidationSupported: true,
    protocols: { compoundV2: COMMON_PROTOCOLS.moonwell },
    color: { light: '#0000FF', dark: '#0000FF' },
    wrappedTokensToPeripheralSymbols: {
      [baseAddresses.wNative]: ['ETH'],
      [baseAddresses.verUsdc]: ['USDC'],
      [baseAddresses.testUsdc]: ['USDC']
    }
  },
  [Web3ChainId.Polygon]: {
    color: { light: '#6C00F6', dark: '#6C00F6' },
    wrappedTokensToPeripheralSymbols: { [polygonAddresses.wNative]: ['POL'] }
  },
  [Web3ChainId.Unichain]: {
    color: { light: '#f72ac9', dark: '#f72ac9' },
    wrappedTokensToPeripheralSymbols: { [unichainAddresses.wNative]: ['ETH'] }
  },
  [Web3ChainId.Arbitrum]: {
    name: 'Arbitrum',
    color: { light: '#12a8ff', dark: '#12a8ff' },
    wrappedTokensToPeripheralSymbols: { [arbitrumAddresses.wNative]: ['ETH'] }
  },
  [Web3ChainId.Optimism]: {
    color: { light: '#f01a37', dark: '#f01a37' },
    wrappedTokensToPeripheralSymbols: { [optimismAddresses.wNative]: ['ETH'] }
  },
  [Web3ChainId.Worldchain]: {
    color: { light: '#000000', dark: '#FFFFFF' },
    wrappedTokensToPeripheralSymbols: { [worldchainAddresses.wNative]: ['ETH'] }
  },
  [Web3ChainId.Plume]: {
    color: { light: '#ff3c00', dark: '#ff3c00' },
    wrappedTokensToPeripheralSymbols: { [plumeAddresses.wNative]: ['PLUME'] }
  },
  [Web3ChainId.Katana]: {
    color: { light: '#8A5400', dark: '#f7ff0a' },
    wrappedTokensToPeripheralSymbols: { [katanaAddresses.wNative]: ['ETH'] }
  },
  [Web3ChainId.Hyperevm]: {
    color: { light: '#97FCE4', dark: '#97FCE4' },
    wrappedTokensToPeripheralSymbols: { [hyperliquidAddresses.wNative]: ['HYPE'] }
  },
  [Web3ChainId.Monad]: {
    color: { light: '#6E54FF', dark: '#6E54FF' },
    wrappedTokensToPeripheralSymbols: { [monadAddresses.wNative]: ['MON'] }
  },
  [Web3ChainId.Stable]: {
    color: { light: '#012321', dark: '#5C7F7A' }
  },
  [Web3ChainId.Tempo]: {
    color: { light: '#6366F1', dark: '#818CF8' }
  },
  [Web3ChainId.Robinhood]: {
    color: { light: '#6B7280', dark: '#9CA3AF' }
  },
  [Web3ChainId.Arc]: {
    isNew: true,
    color: { light: '#205C81', dark: '#205C81' }
  }
} as const satisfies Partial<Record<Web3ChainId, ChainOverlay>>

type VaultsChainId = keyof typeof vaultsChains

export const katana = chains[Web3ChainId.Katana]
export const hyperliquid = chains[Web3ChainId.Hyperevm]
export const monad = chains[Web3ChainId.Monad]
export const tempo = chains[Web3ChainId.Tempo]
export const robinhood = chains[Web3ChainId.Robinhood]
export const stable = chains[Web3ChainId.Stable]
export const arc = chains[Web3ChainId.Arc]

export type ChainIdentifier = (typeof chains)[VaultsChainId]['custom']['morphoChainSlug']

type VaultsInterfaceChain = InterfaceChain & {
  chain: { id: (typeof chains)[VaultsChainId]['id'] }
  chainIdentifier: ChainIdentifier
}

function toInterfaceChain(id: VaultsChainId): VaultsInterfaceChain {
  const chain = chains[id]
  const overlay: ChainOverlay = vaultsChains[id]
  const shared: Pick<
    ChainConfig,
    'morphoChainSlug' | 'safeShortName' | 'simplePermitTokens' | 'gasToken'
  > = chain.custom
  return {
    chain: {
      ...chain,
      ...(isDefined(overlay.name) && { name: overlay.name }),
      blockExplorers: {
        default: {
          name: chain.blockExplorers.default.name,
          url: ChainUtils.getExplorerUrl(chain.id)
        }
      }
    },
    // oxlint-disable-next-line typescript/consistent-type-assertions
    chainIdentifier: shared.morphoChainSlug as ChainIdentifier,
    isPreLiquidationSupported: overlay.isPreLiquidationSupported ?? false,
    protocols: { ...DEFAULT_PROTOCOLS, ...overlay.protocols },
    withSimplePermit: new Set(shared.simplePermitTokens ?? []),
    ...(isDefined(shared.gasToken) && { gasToken: shared.gasToken }),
    safeIdentifier: shared.safeShortName,
    vaultVersion: getMetaMorphoFactories(id),
    merklAddress: MERKL_DISTRIBUTOR_ADDRESS,
    color: overlay.color,
    wrappedTokensToPeripheralSymbols: overlay.wrappedTokensToPeripheralSymbols ?? {},
    ...(overlay.isNew === true && { isNew: true })
  }
}

const chainMetadata: Partial<Record<ChainId, VaultsInterfaceChain>> = Object.fromEntries(
  // oxlint-disable-next-line typescript/consistent-type-assertions
  (Object.keys(vaultsChains).map(Number) as VaultsChainId[]).map(id => [id, toInterfaceChain(id)])
)

const deployedChains = Object.values(chainMetadata).filter(chain => chain !== undefined)

export class UnknownChainError extends Error {
  constructor(chainId: ChainId | string) {
    super(`Chain "${chainId}" not found`)
  }
}

/**
 * Utils for the interface chains
 */
export const InterfaceChainUtils = {
  /**
   * List of chains with contract addresses deployed
   */
  DEPLOYED_CHAINS: deployedChains,

  /**
   * Get the metadata for a chain
   * @param chainId - The id of the chain
   * @returns The metadata for the chain
   */
  getMetadata: (chainId: ChainId): VaultsInterfaceChain => {
    const chain = chainMetadata[chainId]

    if (!isDefined(chain)) {
      throw new UnknownChainError(chainId)
    }
    return chain
  },

  /**
   * Get the safe identifier for a chain
   * @param chainId - The id of the chain
   * @returns The safe identifier for the chain
   */
  getSafeIdentifier: (chainId: ChainId) => {
    const chain = chainMetadata[chainId]

    if (!isDefined(chain)) {
      throw new UnknownChainError(chainId)
    }

    return chain.safeIdentifier
  },

  /**
   * Get the chain id by the chain name
   * @param identifier - The identifier of the chain
   * @returns The chain id
   */
  getIdByIdentifier: (identifier: string): ChainId => {
    const chainInfo = deployedChains.find(chain => chain.chainIdentifier === identifier)

    if (!isDefined(chainInfo)) {
      throw new UnknownChainError(identifier)
    }

    return chainInfo.chain.id
  },

  /**
   * Get the chain name by the chain id
   * @param chainId - The id of the chain
   * @returns The name of the chain
   */
  getIdentifierById: (chainId: ChainId): ChainIdentifier => {
    const chainInfo = deployedChains.find(chain => chain.chain.id === chainId)

    if (!isDefined(chainInfo)) {
      throw new UnknownChainError(chainId)
    }

    return chainInfo.chainIdentifier
  },

  /**
   * Try to get the chain id by the chain name
   * @param idOrName - The id or name of the chain
   * @returns The chain id
   */
  tryGetChain: (idOrIdentifier: string | number | undefined) => {
    if (idOrIdentifier === undefined) return undefined

    let _idOrName = idOrIdentifier

    if (typeof idOrIdentifier === 'string') {
      _idOrName = idOrIdentifier.trim().toLowerCase()
    }

    const chainInfo = deployedChains.find(
      chain => chain.chain.id === _idOrName || chain.chainIdentifier === _idOrName
    )

    return chainInfo ? chainInfo.chain : null
  },

  /**
   * Get the protocols for a chain
   * @param chainId - The id of the chain
   * @returns The protocols for the chain
   */
  getProtocol: ({ chainId, name }: { chainId: ChainId; name: ProtocolName }) => {
    const chain = chainMetadata[chainId]

    if (!isDefined(chain)) {
      throw new UnknownChainError(chainId)
    }

    return chain.protocols[name]
  },

  /**
   * Check if a chain is pre-liquidation supported
   * @param chainId - The id of the chain
   * @returns True if the chain is pre-liquidation supported, false otherwise
   */
  isPreLiquidationSupported: (chainId: ChainId) => {
    const chain = chainMetadata[chainId]

    if (!isDefined(chain)) {
      throw new UnknownChainError(chainId)
    }

    return chain.isPreLiquidationSupported
  },

  /**
   * Get the assets with simple permit for a chain
   * @param chainId - The id of the chain
   * @returns The assets with simple permit for the chain
   */
  getAssetsWithSimplePermit: (chainId: ChainId) => {
    const chain = chainMetadata[chainId]

    if (!isDefined(chain)) {
      throw new UnknownChainError(chainId)
    }

    return chain.withSimplePermit
  },

  /**
   * Get the wrapped tokens to peripheral symbols for a chain
   * @param chainId - The id of the chain
   * @returns The wrapped tokens to peripheral symbols for the chain
   */
  getWrappedTokensToPeripheralSymbols: ({
    chainId,
    tokenAddress
  }: {
    chainId: ChainId
    tokenAddress: Address
  }) => {
    const chain = chainMetadata[chainId]

    if (!isDefined(chain)) {
      throw new UnknownChainError(chainId)
    }

    const wrappedTokensToPeripheralSymbols = chain.wrappedTokensToPeripheralSymbols

    if (!isDefined(wrappedTokensToPeripheralSymbols)) {
      return []
    }

    return (
      wrappedTokensToPeripheralSymbols[
        // oxlint-disable-next-line typescript/consistent-type-assertions
        tokenAddress as keyof typeof wrappedTokensToPeripheralSymbols
      ] || []
    )
  },

  /**
   * Get the vault version by the address
   * @param chainId - The id of the chain
   * @param address - The address of the vault
   * @returns The vault version
   */
  getVaultVersionByAddress: (chainId: ChainId, address: Address): VaultVersion | undefined => {
    const chain = chainMetadata[chainId]

    if (!isDefined(chain)) {
      throw new UnknownChainError(chainId)
    }

    // oxlint-disable-next-line typescript/consistent-type-assertions
    return Object.entries(chain.vaultVersion).find(([, vaultAddress]) =>
      vaultAddress?.toLowerCase().endsWith(address.toLowerCase())
    )?.[0] as VaultVersion | undefined
  },

  /**
   * Get the merkl address for a chain
   * @param chainId - The id of the chain
   * @returns The merkl address for the chain
   */
  getMerklAddress: (chainId: ChainId) => {
    const chain = chainMetadata[chainId]

    if (!isDefined(chain)) {
      throw new UnknownChainError(chainId)
    }

    return chain.merklAddress
  },

  /**
   * Get the color for a chain
   * @param chainId - The id of the chain
   * @returns The color for the chain
   */
  getColor: (chainId: ChainId) => {
    const chain = chainMetadata[chainId]

    if (!isDefined(chain)) {
      throw new UnknownChainError(chainId)
    }

    return chain.color
  },

  isDeployedOnChain: {
    /**
     * Check if a chain id is supported by the interface
     * @param chainId - The id of the chain
     * @returns True if the chain id is supported, false otherwise
     */
    id: (chainId: ChainId): chainId is ChainId => {
      return deployedChains.map(({ chain }) => chain.id).find(id => id === chainId) !== undefined
    },

    /**
     * Check if a chain identifier is supported by the interface
     * @param identifier - The identifier of the chain
     * @returns True if the chain identifier is supported, false otherwise
     */
    identifier: (identifier: string): identifier is ChainIdentifier => {
      return deployedChains.some(chain => chain.chainIdentifier === identifier)
    }
  }
}
